import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../../services/apiClient";

// Fetch Subscriptions
export const fetchExternalSubscriptions = createAsyncThunk(
  'externalSubscriptions/fetchAll',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/canvas/${canvasId}/external-subscriptions?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);
 
// Create external subscription
export const createExternalSubscription = createAsyncThunk(
  'externalSubscriptions/create',
  async ({ canvasId, subscriptionData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/canvas/${canvasId}/external-subscriptions`,
        subscriptionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Update external subscription
export const updateExternalSubscription = createAsyncThunk(
  'externalSubscriptions/update',
  async ({ canvasId, subscriptionId, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/canvas/${canvasId}/external-subscriptions/${subscriptionId}`,
        updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Delete external subscription
export const deleteExternalSubscription = createAsyncThunk(
  'externalSubscriptions/delete',
  async ({ canvasId, subscriptionId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(
        `/canvas/${canvasId}/external-subscriptions/${subscriptionId}`);
      return subscriptionId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload subscriptions from file
export const uploadExternalSubscriptions = createAsyncThunk(
  'externalSubscriptions/upload',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      if (!(file instanceof File)) {
        throw new Error(`Expected File object but got ${typeof file}: ${JSON.stringify(file)}`);
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/canvas/${canvasId}/external-subscriptions/upload`,
        formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);


export const subscriptionOverview = createAsyncThunk(
  'externalSubscription/overview',
  async ({canvasId}, {rejectWithValue})=>{
    try{
      const res = await apiClient.get(`canvas/${canvasId}/external-subscriptions/overview`);
      return res.data
    }catch(e){
      return rejectWithValue(e)
    }
  }

)

const externalSubscriptionSlice = createSlice({
  name: 'externalSubscriptions',
  initialState: {
    subscriptions: [],
    pagination: { total: 0, page: 1, pages: 1 },
    loading: false,
    error: null,
    overview:null,
  },
  reducers: {
    resetExternalSubscriptionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchExternalSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExternalSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.subscriptions || action.payload.items || [];
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchExternalSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createExternalSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExternalSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.subscriptions.push(action.payload);
      })
      .addCase(createExternalSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateExternalSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExternalSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.subscriptions.findIndex(s => s._id === action.payload._id);
        if (idx !== -1) state.subscriptions[idx] = action.payload;
      })
      .addCase(updateExternalSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteExternalSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExternalSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = state.subscriptions.filter(s => s._id !== action.payload);
      })
      .addCase(deleteExternalSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload
      .addCase(uploadExternalSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadExternalSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = [...state.subscriptions, ...(action.payload?.subscriptions || action.payload?.items || [])];
      })
      .addCase(uploadExternalSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(subscriptionOverview.fulfilled, (state,action)=>{
        state.loading = false;
        state.overview = action.payload;
      })
  }
});

export const { resetExternalSubscriptionError } = externalSubscriptionSlice.actions;
export const selectExternalSubscriptions = state => state.externalSubscriptions.subscriptions;
export const selectExternalSubscriptionLoading = state => state.externalSubscriptions.loading;
export const selectExternalSubscriptionPagination = state => state.externalSubscriptions.pagination;
export const selectSubscriptionOverview = state => state.externalSubscriptions.overview;
export default externalSubscriptionSlice.reducer;